import {
  DashInviteRoleEnum,
  DashInviteOrderingInput,
  DashInvite,
  InvitesQuery,
} from "@/__generated__/graphql";
import { useQuery } from "@apollo/client";
import { INVITES_BY_ROLE } from "@/api/queries/invites";

interface InvitesArgs {
  orgId?: string;
  roles: DashInviteRoleEnum[];
  first?: number;
  last?: number;
  after?: string | null;
  before?: string | null;
  ordering: DashInviteOrderingInput[];
}

export function useInvites(variables: InvitesArgs) {
  const { orgId, ...rest } = variables;
  const { data, loading, refetch } = useQuery(INVITES_BY_ROLE, {
    variables: {
      orgId: orgId ?? "",
      ...rest,
    },
    skip: !variables.orgId,
  });

  return { data: processData(data), loading, refetch };
}

interface InviteData {
  invites: Partial<DashInvite>[];
  totalCount: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
  startCursor?: string | null;
  endCursor?: string | null;
  cursor?: string | null;
}

function processData(data?: InvitesQuery): InviteData {
  if (data?.node?.__typename !== "Organization") {
    return { invites: [], totalCount: 0 };
  }
  const invites = data.node.dashInvites;
  const inviteData = invites.edges
    ?.filter((edge) => edge?.__typename === "DashInviteEdge")
    .map((edge) => {
      if (edge?.__typename === "DashInviteEdge" && edge.node) {
        const { node } = edge;
        return {
          id: node.id ?? "",
          email: node.email,
          firstName: node.firstName ?? "",
          invitedAt: node.invitedAt
            ? new Date(node.invitedAt * 1000)
            : undefined,
          lastName: node.lastName ?? "",
          phoneNumber: node.phoneNumber,
          role: node.role,
        };
      }
    })
    .filter((map) => !!map);
  return {
    invites: inviteData ?? [],
    totalCount: invites?.totalCount ?? 0,
    hasPreviousPage: invites?.pageInfo?.hasPreviousPage,
    hasNextPage: invites?.pageInfo?.hasNextPage,
    startCursor: invites?.pageInfo?.startCursor,
    endCursor: invites?.pageInfo?.endCursor,
    cursor: invites?.pageInfo?.endCursor,
  };
}
